import {
  format,
  isSameDay,
  isThisWeek,
  isThisYear,
  isToday,
  isYesterday,
  parseISO,
} from "date-fns";
import { getTimezoneOffset } from "date-fns-tz";
import { SupportedCountry } from "entities/domain/admin/merchants/merchant-domain";
import ConversationDomain from "entities/domain/conversations/conversation-domain";
import { useEffect, useRef, useState } from "react";
import axios from "../services/http";
import { RequestType } from "../services/request-type";

export const colorList = [
  "yellow",
  "pink",
  "blue",
  "purple",
  "green",
  "cyan",
  "teal",
  "red",
  "orange",
];

export const maxAllowedSize = 15 * 1024 * 1024; // 15MB

/**
 * Normalizes a string for comparison so that:
 * - All line ending styles (Windows CRLF, Unix LF, old Mac CR, Unicode separators) become plain \n
 * - Number and position of newlines is 100% preserved (so 3 blank lines still = 3 blank lines)
 * - BOM and other invisible garbage that fucks with equality is stripped
 * - Works on Windows, Linux, macOS, old Mac files, browser textareas, databases, CKEditor, whatever the fuck throws at you in 2026
 */
export function normalizeTextForComparison(str: string) {
  if (typeof str !== "string") return "";

  return (
    str
      // Remove BOM (invisible at start of some files/databases)
      .replace(/\uFEFF/g, "")
      // Convert EVERY possible line ending to single \n
      // This covers: \r\n (Windows), \r (old Mac), \n (Unix), \u2028, \u2029 (Unicode)
      .replace(/\r\n|\r|\u2028|\u2029/g, "\n")
      // Optional but recommended: collapse multiple spaces/tabs inside lines (but NOT newlines)
      // .replace(/[ \t]+/g, ' ')
      .trim()
  ); // keep your .trim() behavior
}

export const request = async <T = any>(
  type: RequestType.GET,
  accessToken: string,
  path: string,
  payload = {},
  signal?: AbortSignal
) =>
  axios[type]<T>(`${path}`, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
    signal,
    ...payload,
  });

export const requestBlob = async <T = any>(
  type: RequestType.GET,
  accessToken: string,
  path: string,
  payload = {},
  signal?: AbortSignal
) =>
  axios[type]<T>(`${path}`, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
    responseType: "blob",
    signal,
    ...payload,
  });

export const publicRequest = async <T = any>(
  type: RequestType = RequestType.GET,
  path: string,
  payload = {},
  headers = {},
  signal?: AbortSignal
) =>
  axios[type]<T>(
    `${path}`,
    {
      ...payload,
    },
    { headers, signal }
  );

export const mutationRequest = async <T = any>(
  type: RequestType,
  accessToken: string,
  path: string,
  payload: object | FormData = {},
  contentType: string = "application/json;charset=UTF-8",
  headers: { [key: string]: string } = {},
  signal?: AbortSignal
) => {
  return axios[type]<T>(`${path}`, payload, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": contentType,
      ...headers,
    },
    signal,
  });
};

export const publicMutationRequest = async <T = any>(
  type: RequestType.POST,
  path: string,
  payload = {},
  signal?: AbortSignal
) =>
  axios[type]<T>(
    `${path}`,
    {
      ...payload,
    },
    { signal }
  );

export const postRequest = async <T = any>(
  accessToken: string,
  path: string,
  payload = {},
  signal?: AbortSignal
) =>
  axios[RequestType.POST]<T>(
    `${path}`,
    {
      ...payload,
    },
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      signal,
    }
  );

export const putRequest = async <T = any>(
  type: RequestType.PUT,
  accessToken: string,
  path: string,
  payload = {},
  signal?: AbortSignal
) =>
  axios[type]<T>(
    `${path}`,
    {
      ...payload,
    },
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      signal,
    }
  );

export const patchRequest = async <T = any>(
  type: RequestType.PATCH,
  accessToken: string,
  path: string,
  payload: object | FormData = {},
  contentType: string = "application/json;charset=UTF-8",
  signal?: AbortSignal
) =>
  axios[type]<T>(`${path}`, payload, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": contentType,
    },
    signal,
  });

export const deleteRequest = async <T = any>(
  type: RequestType.DELETE,
  accessToken: string,
  path: string,
  payload = {},
  signal?: AbortSignal
) =>
  axios[type]<T>(`${path}`, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
    data: payload,
    signal,
  });

export function dateToFromNowDaily(date: string) {
  const displayDate = parseISO(date);

  if (isToday(displayDate)) {
    return format(displayDate, "hh:mm a");
  } else if (isYesterday(displayDate)) {
    return "Yesterday";
  } else if (isThisWeek(displayDate)) {
    return format(displayDate, "eeee"); // Day of the week
  } else {
    return format(displayDate, updateFormatAccordingToCountry("dd/MM/yyyy"));
  }
}

export const getProfileImage = (image?: string | null) => image || "";

export function isMobile() {
  // credit to Timothy Huang for this regex test:
  // https://dev.to/timhuang/a-simple-way-to-detect-if-browser-is-on-a-mobile-device-with-javascript-44j3
  return /Android|webOS|iPhone|iPod|iPad|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  );
}

export const getUsernameInitials = (user: any) =>
  user.user_name.split(" ").length > 1
    ? `${user.user_name.split(" ")[0][0]}${user.user_name.split(" ")[1][0]}`
    : `${user.user_name[0]}`;

export const stripWhitespace = (text: string): string[] => {
  return text.split(" ").filter((e) => e);
};

export function useStateWithCallback<T>(
  initialState: T
): [T, (state: T, cb?: (state: T) => void) => void] {
  const [state, setState] = useState(initialState);
  const cbRef = useRef<((state: T) => void) | undefined>(undefined);

  const setStateCallback = (stateInCallback: T, cb?: (state: T) => void) => {
    cbRef.current = cb;
    setState(stateInCallback);
  };

  useEffect(() => {
    if (cbRef.current) {
      cbRef.current(state);
      cbRef.current = undefined;
    }
  }, [state]);

  return [state, setStateCallback];
}

export const getMerchantTimeZoneByCountryCode = (
  countryCode: SupportedCountry
): string => {
  const timeZones: Record<SupportedCountry, string> = {
    [SupportedCountry.GB]: "Europe/London",
    [SupportedCountry.IE]: "Europe/Dublin",
    [SupportedCountry.US]: "America/New_York",
    [SupportedCountry.CA]: "America/Toronto",
    [SupportedCountry.AU]: "Australia/Sydney",
    [SupportedCountry.BH]: "Asia/Bahrain",
    [SupportedCountry.OM]: "Asia/Muscat",
    [SupportedCountry.IQ]: "Asia/Baghdad",
    [SupportedCountry.AE]: "Asia/Dubai",
  };

  return timeZones[countryCode];
};

export const getUTCOffsetByCountryCode = (
  countryCode: SupportedCountry
): number => {
  const timezone = getMerchantTimeZoneByCountryCode(countryCode);

  return getTimezoneOffset(timezone) / 1000 / 60 / 60;
};

export const getDayNameByIndex = (index: number): string => {
  const days = [
    "sunday",
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
  ];

  const day = days[index];

  return day || days[0];
};

export const isLocalEnvironment = (): boolean => {
  return window.location.host.includes("localhost");
};

export const isProductionEnvironment = (): boolean => {
  return process.env.REACT_APP_ENVIRONMENT === "production";
};

export const isFuzeyMerchant = (merchantName: string): boolean => {
  if (!merchantName) {
    return false;
  }

  return /.*Fuzey.*/i.test(merchantName);
};

export const isLondonLocksMerchant = (merchantId: number): boolean => {
  return merchantId === 127;
};

// Merchant groups with the YK (YKClient) CRM integration enabled.
export const YK_ENABLED_GROUP_IDS: readonly string[] = [
  "761f00de-964a-4c77-97a1-6cb49754024e", // Ford Bahrain
  "e7149f5e-1f51-4d6f-81ec-7a86488e3426", // Asia Motors
  "dd0ea6ea-2231-48f8-985a-cb2bb5f0a125", // YK Almoayyed (group "Nissan Bahrain")
];

export const isYkMerchant = (groupId: string | null | undefined): boolean => {
  return groupId != null && YK_ENABLED_GROUP_IDS.includes(groupId);
};

export const isMobileApp = (): boolean => {
  if (typeof window === "undefined") {
    return false;
  }

  // Rely on an ephemeral session flag set by the native booter when running inside the container
  try {
    return sessionStorage.getItem("fuzey:is_native_session") === "1";
  } catch (_e) {
    return false;
  }
};

export async function checkMediaValidity(
  url: string,
  onErrorCallback: () => void
) {
  try {
    const requestUrl =
      typeof window === "undefined"
        ? undefined
        : new URL(url, window.location.href);
    const isCrossOrigin =
      requestUrl !== undefined && requestUrl.origin !== window.location.origin;

    const response = await fetch(url, {
      method: "HEAD",
      mode: isCrossOrigin ? "no-cors" : "cors",
    });

    if (response.type === "opaque") {
      return;
    }

    if (!response.ok) {
      throw new Error("Invalid URL");
    }
  } catch (error) {
    onErrorCallback();
  }
}

export const isIOSPlatform = (): boolean => {
  return /iPhone|iPod|iPad/i.test(navigator.userAgent);
};

export const isIOSTablet = (): boolean => {
  return /iPad/i.test(navigator.userAgent);
};

export const isAndroid = (): boolean => {
  return /Android/i.test(navigator.userAgent);
};

export const isIOSMobileApp = (): boolean => {
  return isMobileApp() && isIOSPlatform();
};

export const getDisplayPrettyDate = (date: Date): string => {
  if (isToday(date)) {
    return format(date, "hh:mm a");
  } else if (isYesterday(date)) {
    return `Yesterday ${format(date, "hh:mm a")}`;
  } else if (isThisWeek(date)) {
    return format(date, "eeee hh:mm a");
  } else {
    return format(
      date,
      `${updateFormatAccordingToCountry("dd/MM/yyyy")} hh:mm a`
    );
  }
};

export const getMessageDayHeader = (date: Date): string => {
  if (isToday(date)) {
    return "Today";
  }
  if (isYesterday(date)) {
    return "Yesterday";
  }
  if (isThisWeek(date)) {
    return format(date, "eeee");
  }
  if (isThisYear(date)) {
    return format(date, updateFormatAccordingToCountry("d MMMM"));
  }
  return format(date, updateFormatAccordingToCountry("d MMMM yyyy"));
};

export const isSameMessageDay = (a: Date, b: Date): boolean => isSameDay(a, b);

export function humanizeSnakeCase(input: string): string {
  return input
    .replace(/_/g, " ")
    .split(" ")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

export function calculateTypingTime(text: string): string {
  const words = text.split(" ").length;
  const averageTypingSpeedWordsPerSecond = 0.67; // average typing speed - 40 words per minute
  let seconds = Math.round(words / averageTypingSpeedWordsPerSecond);

  if (seconds < 60) {
    return `${seconds} seconds`;
  } else {
    const minutes = Math.floor(seconds / 60);
    seconds %= 60;
    return `${minutes} minutes ${seconds} seconds`;
  }
}

function findPositions(dateFormat: string): {
  mmPos: number[];
  ddPos: number[];
} | null {
  const mmStart = dateFormat.toUpperCase().indexOf("MM");
  const mmEnd = mmStart + 2;
  const ddStart = dateFormat.toUpperCase().indexOf("DD");
  const ddEnd = ddStart + 2;

  if (mmStart === -1 || ddStart === -1) {
    return null; // 'MM' or 'DD' not found in the string
  }

  return {
    mmPos: [mmStart, mmEnd],
    ddPos: [ddStart, ddEnd],
  };
}

function americanizeDateFormat(dateFormat: string): string | null {
  if (!dateFormat) {
    return null;
  }

  const needsAmerica = dateFormat.includes("dd") && dateFormat.includes("MM");

  if (!needsAmerica) {
    return null;
  }

  const positionsOfMonthAndDay = findPositions(dateFormat);

  if (!positionsOfMonthAndDay) {
    return null;
  }

  const { mmPos, ddPos } = positionsOfMonthAndDay;

  const mm = dateFormat.substring(mmPos[0], mmPos[1]);
  const dd = dateFormat.substring(ddPos[0], ddPos[1]);

  const newFormat = dateFormat.replace(mm, "dd").replace(dd, "MM");

  return newFormat;
}

export function updateFormatAccordingToCountry(dateFormat: string): string {
  if (!dateFormat) {
    return dateFormat;
  }

  const country = localStorage.getItem("fuzey:merchant:country");

  if (!country) {
    return dateFormat;
  }

  if (country === "USA") {
    const newFormat = americanizeDateFormat(dateFormat);

    if (newFormat) {
      return newFormat;
    }
  }

  return dateFormat;
}

export const containsPrefix = (text: string, prefix: string) =>
  text.substring(0, prefix.length) === prefix;

export const containsSuffix = (text: string, suffix: string) =>
  text.substring(text.length - suffix.length) === suffix;

export const convertSecondsToHoursMinutesSeconds = (
  seconds: number
): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;

  return `${hours > 0 ? `${hours}:` : ""}${
    minutes > 9 ? minutes : `0${minutes}`
  }:${remainingSeconds > 9 ? remainingSeconds : `0${remainingSeconds}`}`;
};

function countNonNullAttributes<T>(
  obj: T,
  numericAttributes: (keyof T)[]
): number {
  let count = 0;
  for (const key in obj) {
    if (!numericAttributes.includes(key)) {
      // eslint-disable-next-line no-continue
      continue;
    }

    if (obj[key] !== null) {
      count++;
    }
  }
  return count;
}

function compareNumericAttributes<T>(
  a: T,
  b: T,
  attributes: (keyof T)[],
  sorting: "asc" | "desc"
): number {
  for (const attr of attributes) {
    if (typeof a[attr] === "number" && typeof b[attr] === "number") {
      if (a[attr] !== b[attr]) {
        if (sorting === "asc") {
          return (
            (a[attr] as unknown as number) - (b[attr] as unknown as number)
          );
        } else {
          return (
            (b[attr] as unknown as number) - (a[attr] as unknown as number)
          );
        }
      }
    }
  }
  return 0;
}

export function sortObjects<T>(
  objects: T[],
  numericAttributes: (keyof T)[],
  sorting: "asc" | "desc" = "desc"
): T[] {
  return [...objects].sort((a, b) => {
    const nonNullComparison =
      countNonNullAttributes(b, numericAttributes) -
      countNonNullAttributes(a, numericAttributes);
    if (nonNullComparison !== 0) {
      return nonNullComparison;
    }
    return compareNumericAttributes(a, b, numericAttributes, sorting);
  });
}

export const removePrefixAndSuffix = (
  text: string,
  prefix: string,
  suffix: string
) => {
  if (prefix && containsPrefix(text, prefix)) {
    text = text.substring(prefix.length + 1);
  }

  if (suffix && containsSuffix(text, suffix)) {
    text = text.substring(0, text.length - suffix.length);
  }

  return text;
};

export function humanizeCamelCase(input: string): string {
  // First, replace capital letters with spaces + the capital letter.
  let result = input.replace(/([A-Z])/g, " $1");

  // Trim any leading spaces and then capitalize the first letter.
  result = result.charAt(0).toUpperCase() + result.slice(1);

  return result;
}

export function flattenObject(potentiallyNestedObject: {
  [key: string]: any;
}): { [key: string]: any } {
  const flattened: { [key: string]: any } = {};

  Object.keys(potentiallyNestedObject).forEach((key) => {
    const value = potentiallyNestedObject[key];

    if (typeof value === "object" && value !== null && !Array.isArray(value)) {
      Object.assign(flattened, flattenObject(value));
    } else {
      flattened[key] = value;
    }
  });

  return flattened;
}

export const getChannelsFromConversations = (
  conversations: ConversationDomain[],
  selectedConversationIds: number[]
): string[] => {
  const selectedConversations = conversations.filter((c) =>
    selectedConversationIds.includes(c.id)
  );

  const selectedConversationsChannels = selectedConversations.map(
    (c) => c.customerChannelId
  );

  return selectedConversationsChannels;
};

export const replaceCustomFields = (
  text: string | null,
  customFields: { [key: string]: string }
): string | null => {
  if (!text) {
    return text;
  }

  let newText = `${text}`;

  Object.keys(customFields).forEach((field) => {
    newText = newText.replaceAll(`{${field}}`, customFields[field]);
  });

  return newText;
};

export function getUniqueListBy<T>(arr: T[], key: keyof T): T[] {
  return [...new Map(arr.map((item) => [item[key], item])).values()];
}

export function isMacintosh() {
  return navigator.userAgent.indexOf("Mac") > -1;
}

export function isWindows() {
  return navigator.userAgent.indexOf("Win") > -1;
}

export function getColorForString(value: string): string {
  let total = 0;

  for (let i = 0; i < value.length; i++) {
    total += value.charCodeAt(i);
  }

  const index = total % colorList.length;

  return colorList[index];
}
